import React, { CSSProperties, useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import StopIcon from '@mui/icons-material/Stop';
import { AxiosError } from 'axios';
import { Question, Quiz } from '../../models/Quiz';
import useToast from '../../hooks/useToast';
import { handleListenQuestion } from './utils';
import { EventQuestionsAnswers } from './EventQuestionsAnswers';
import api from '../../services/api';

type QuizComponentProps = {
  quiz: Quiz;
  userSessionId: string;
  style?: CSSProperties;
};

export const EventQuizComponent = ({
  quiz,
  style,
  userSessionId,
}: QuizComponentProps) => {
  const [listening, setListening] = useState(false);
  const [answers, setAnswers] = useState<{ [questionId: string]: string }>({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userCoins, setUserCoins] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [userCode, setUserCode] = useState('');
  const [isRunning, setIsRunning] = useState(false);

  const { addToast } = useToast();
  const currentQuestion = quiz.questions[currentQuestionIndex];

  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAnswers({
      ...answers,
      [currentQuestion.id]: event.target.value,
    });
  };

  const handleNext = () => {
    if (currentQuestionIndex < quiz.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  // [min, max, multiplier]
  const TIME_MULTIPLIERS: [number, number, number][] = [
    [0, 30, 1.3], // muito rápido
    [31, 60, 1.2], // rápido
    [61, 90, 1.1], // médio
    [91, 120, 1.0], // lento
    [121, Infinity, 0.9], // muito lento
  ];

  const getTimeMultiplier = () => {
    for (const [min, max, multiplier] of TIME_MULTIPLIERS) {
      if (elapsedTime >= min && elapsedTime <= max) {
        return multiplier;
      }
    }
    return 1.0;
  };

  const calculateScore = () => {
    const basePoints = 6;
    let sucesses = 0;

    quiz.questions.forEach(question => {
      if (answers[question.id] === question.target) {
        sucesses += 2.25;
      }
    });

    const timeMultiplier = getTimeMultiplier();
    const finalScore = sucesses * basePoints * timeMultiplier;
    const coins = Math.ceil(finalScore);
    setCorrectAnswers(sucesses);
    return coins;
  };

  const handleSubmit = async () => {
    setIsRunning(false);
    const answerLength = Object.keys(answers).length;
    if (answerLength < quiz.questions.length) {
      addToast({
        type: 'error',
        title: 'Erro ao enviar Quiz',
        description: 'Você deve responder todas as perguntas!',
      });
    }
    const balance = calculateScore();

    setUserCoins(balance);

    const payload = {
      userSessionId,
      balance,
    };

    try {
      const { data } = await api.post<{
        userCode: string;
      }>('/quiz/event/complete', payload);

      setUserCode(data.userCode);
    } catch (error) {
      if (error instanceof AxiosError) {
        const alreadyRegisted =
          error.response?.data.code === 'USER_ALREADY_EXISTS';
        if (alreadyRegisted) {
          addToast({
            type: 'error',
            title: 'Erro ao enviar Quiz',
            description: 'Você já enviou este quiz!',
          });
        }
      }
      return;
    }

    addToast({
      type: 'success',
      title: 'Quiz enviado com sucesso!',
      description: `Você ganhou ${balance} moedas! Seu tempo foi de ${elapsedTime} segundos.`,
    });

    setSeeQuestionsAnswers(true);
    setCanDoQuiz(false);
  };

  const [canDoQuiz, setCanDoQuiz] = useState<boolean>(false);
  const [seeQuestionsAnswers, setSeeQuestionsAnswers] =
    useState<boolean>(false);

  const handleStartQuiz = () => {
    if (correctAnswers > 0) {
      setSeeQuestionsAnswers(true);
    }
    setCanDoQuiz(true);
    setElapsedTime(0);
    setIsRunning(true);
  };

  useEffect(() => {
    window.speechSynthesis.cancel();
    setAnswers({});
    setCurrentQuestionIndex(0);
  }, [quiz.id]);

  useEffect(() => {
    let interval: number | undefined;

    if (isRunning) {
      interval = window.setInterval(() => {
        setElapsedTime(prevTime => prevTime + 1);
      }, 1000);
    }

    return () => {
      if (interval !== undefined) {
        window.clearInterval(interval);
      }
    };
  }, [isRunning]);
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(
      2,
      '0',
    )}`;
  };

  const reset = () => {
    setCanDoQuiz(false);
  };

  return (
    <QuizContainer width={style?.width?.toString()}>
      {!seeQuestionsAnswers && canDoQuiz ? (
        <>
          <Progress>
            <p>
              {currentQuestionIndex + 1} / {quiz.questions.length}
            </p>
          </Progress>
          <QuestionDescriptionContainer>
            <ListenQuestion>
              <div />
              {isRunning && (
                <TimerDisplay>{formatTime(elapsedTime)}</TimerDisplay>
              )}
              <ListButton
                type="button"
                onClick={() =>
                  handleListenQuestion(
                    currentQuestion as Question,
                    listening,
                    setListening,
                  )
                }
              >
                {listening ? (
                  <StopIcon fontSize="small" />
                ) : (
                  <VolumeUpIcon fontSize="small" />
                )}
              </ListButton>
            </ListenQuestion>
            <QuestionDescription size="14px">
              {currentQuestion.description}
            </QuestionDescription>
          </QuestionDescriptionContainer>
          <Options>
            <OptionLabel isChecked={answers[currentQuestion.id] === 'optionA'}>
              <input
                type="radio"
                name={`question-${currentQuestion.id}`}
                value="optionA"
                checked={answers[currentQuestion.id] === 'optionA'}
                onChange={handleOptionChange}
              />
              {currentQuestion.optionA}
            </OptionLabel>
            <OptionLabel isChecked={answers[currentQuestion.id] === 'optionB'}>
              <input
                type="radio"
                name={`question-${currentQuestion.id}`}
                value="optionB"
                checked={answers[currentQuestion.id] === 'optionB'}
                onChange={handleOptionChange}
              />
              {currentQuestion.optionB}
            </OptionLabel>
            <OptionLabel isChecked={answers[currentQuestion.id] === 'optionC'}>
              <input
                type="radio"
                name={`question-${currentQuestion.id}`}
                value="optionC"
                checked={answers[currentQuestion.id] === 'optionC'}
                onChange={handleOptionChange}
              />
              {currentQuestion.optionC}
            </OptionLabel>
            <OptionLabel isChecked={answers[currentQuestion.id] === 'optionD'}>
              <input
                type="radio"
                name={`question-${currentQuestion.id}`}
                value="optionD"
                checked={answers[currentQuestion.id] === 'optionD'}
                onChange={handleOptionChange}
              />
              {currentQuestion.optionD}
            </OptionLabel>
          </Options>
          <ButtonContainer>
            <Button
              disabled={currentQuestionIndex === 0}
              onClick={handlePrevious}
            >
              &larr; Anterior
            </Button>
            {currentQuestionIndex === quiz.questions.length - 1 ? (
              <Button
                disabled={!answers[currentQuestion.id]}
                onClick={handleSubmit}
              >
                Enviar{' '}
              </Button>
            ) : (
              <Button
                disabled={!answers[currentQuestion.id]}
                onClick={handleNext}
              >
                Próxima &rarr;{' '}
              </Button>
            )}
          </ButtonContainer>
        </>
      ) : (
        !seeQuestionsAnswers && (
          <StartQuizContainer>
            <Icon>
              <AssignmentTurnedInIcon />
            </Icon>
            <QuestionDescription>Quiz - {quiz.title}</QuestionDescription>
            <Button onClick={() => handleStartQuiz()}>Começar Quiz</Button>
          </StartQuizContainer>
        )
      )}

      {seeQuestionsAnswers && (
        <EventQuestionsAnswers
          onBack={() => reset()}
          userCode={userCode}
          questions={quiz.questions}
          userCoins={userCoins}
          studentAnswers={answers || {}}
        />
      )}
    </QuizContainer>
  );
};

const TimerDisplay = styled.span`
  font-size: 14px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.secondary};
  margin-left: 10px;
`;

const ListenQuestion = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
`;

const ListButton = styled.button`
  color: ${({ theme }) => theme.colors.primary};
  background: transparent;
  border: none;
  background-color: transparent;
  cursor: pointer;

  &:hover {
    filter: brightness(1.2);
  }
`;

const QuizScoreContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.2rem;
`;

const Score = styled.span<{ scorePointColor: number }>`
  color: ${({ scorePointColor, theme }) =>
    scorePointColor >= 40 ? theme.colors.primary : theme.colors.alert};
`;

const QuizAnimation = () => keyframes`
  0% { opacity: 0; color: transparent; transform: scale(1.02)}
  50% { color: auto }
  100% { opacity: 100%; transform: scale(1)}
`;

const Icon = styled.div`
  display: flex;
  justify-content: center;
  padding: 1rem;
  background-color: ${({ theme }) => theme.colors.backgroundTertiary};
  border-radius: 18px;
  color: ${({ theme }) => theme.colors.themedTitle};
`;

const StartQuizContainer = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  justify-content: center;
`;

const QuizContainer = styled.div<{ width?: string }>`
  background-color: ${({ theme }) => theme.colors.backgroundSecondary};
  color: ${({ theme }) => theme.colors.text};
  padding: 20px;
  border-radius: 8px;
  width: ${({ width }) => width || '132rem'};
  margin: 0 auto;
  text-align: center;
  animation: ${QuizAnimation} 0.5s ease-in-out;
  /* border-right: ${({ theme }) => `2px solid ${theme.colors.primary}`}; */
`;

const Progress = styled.div`
  font-size: 14px;
  margin-bottom: 5px;
  display: flex;
  justify-content: flex-start;
  color: #aaa;
`;

const QuestionDescriptionContainer = styled.div`
  line-height: 1.5;
  margin-top: 0.5rem;
  justify-content: center;
  width: 100%;
  gap: 2rem;
  padding: 0 1rem;
  overflow: hidden;
`;

const QuestionDescription = styled.h2<{ size?: string }>`
  font-size: ${({ theme, size }) => size || theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.themedTitle};
  margin: 0.5rem 0;
  word-break: break-word;
  white-space: normal;
  max-width: 100%;
  overflow-wrap: break-word;
  text-align: center;
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const OptionLabel = styled.label<{ isChecked?: boolean }>`
  background-color: ${({ theme }) => theme.colors.backgroundTertiary};
  padding: 7px 15px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  cursor: pointer;
  font-family: ${({ theme }) => theme.fonts.inter};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.themedTitle};
  border: ${({ isChecked, theme }) =>
    isChecked ? `2px solid ${theme.colors.secondaryDark}` : 'none'};

  input {
    margin-right: 10px;
    height: 25px;
  }

  &:hover {
    filter: brightness(0.75);
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
  gap: 2rem;
`;

const Button = styled.button`
  background-color: ${({ theme }) => theme.colors.secondaryDark};
  color: #fff;
  border: none;
  padding: 10px 15px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  height: 2.25rem;
  cursor: pointer;
  font-weight: bold;
  transition: background-color 0.1s;

  &:hover {
    background-color: ${({ theme }) => theme.colors.secondary};
  }

  &:disabled {
    background-color: #646161;
    cursor: not-allowed;
  }
  transition: background-color 0.25s;
`;
